import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';

type AppRole = 'admin' | 'creator_management' | 'venue_oversight' | 'platform_control';

interface RoleCheckResult {
  hasRole: boolean;
  isLoading: boolean;
  error: string | null;
  checkRole: (role: AppRole) => Promise<boolean>;
}

export function useRoleCheck(session: Session | null, requiredRole?: AppRole | AppRole[]): RoleCheckResult {
  const [hasRole, setHasRole] = useState(false);
  const [isLoading, setIsLoading] = useState(!!requiredRole);
  const [error, setError] = useState<string | null>(null);

  const checkRole = useCallback(async (role: AppRole): Promise<boolean> => {
    if (!session?.access_token) {
      return false;
    }

    try {
      const { data, error: fnError } = await supabase.functions.invoke('check-user-role', {
        body: { role },
      });

      if (fnError) {
        console.error('Role check function error:', fnError);
        return false;
      }

      return data?.hasRole === true;
    } catch (err) {
      console.error('Role check error:', err);
      return false;
    }
  }, [session?.access_token]);

  const roleKey = Array.isArray(requiredRole) ? requiredRole.join(',') : requiredRole;

  useEffect(() => {
    if (!requiredRole) {
      setIsLoading(false);
      return;
    }

    if (!session) {
      setHasRole(false);
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const verifyRole = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
        let result = false;
        for (const r of roles) {
          if (await checkRole(r)) {
            result = true;
            break;
          }
        }
        
        if (isMounted) {
          setHasRole(result);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to verify role');
          setHasRole(false);
          setIsLoading(false);
        }
      }
    };

    verifyRole();

    return () => {
      isMounted = false;
    };
  }, [session, roleKey, checkRole]);

  return { hasRole, isLoading, error, checkRole };
}
